import {
  FaArrowRight,
  FaCheckCircle,
  FaMinusCircle,
  FaRegCircle,
} from "react-icons/fa";
import React, { useImperativeHandle } from "react";

import { CardTitle } from "../common";
import PropTypes from "prop-types";
import { sendEvent } from "../../../api/api";
import { useParams } from "react-router-dom";

const ReminderCard = React.forwardRef(
  ({ card, topic, messages, w, h }, ref) => {
    const { token } = useParams();

    const [newReminder, setNewReminder] = React.useState("");

    const action = (setSelectedCard) => {
      setSelectedCard(card);
    };

    useImperativeHandle(ref, () => ({
      action,
    }));

    const safeMessages = messages && messages.length > 0 ? messages : [];
    const reminders = safeMessages
      .map((msg) => ({ ...msg, content: msg.content.trim() }))
      .filter((msg) => msg.content.startsWith("["));

    const normalizedReminders = reminders.reduce((acc, msg) => {
      const key = msg.content.slice(3).trim(); // base message text
      const current = acc[key];

      if (
        !current ||
        new Date(msg.timestamp || 0) > new Date(current.timestamp || 0)
      ) {
        acc[key] = msg;
      }
      return acc;
    }, {});

    const finalReminders = Object.values(normalizedReminders).filter(
      (msg) => !msg.content.startsWith("[-]")
    );

    const getStateIcon = (content) => {
      if (content.startsWith("[x] - "))
        return <FaCheckCircle className="text-cambridge-blue" />;
      if (content.startsWith("[-] - "))
        return <FaMinusCircle className="text-red-500" />;
      return <FaRegCircle className="text-gray-500" />;
    };

    const handleToggle = async (content, currentState) => {
      let newState;
      if (currentState === "[x] - ") newState = "[] - ";
      else newState = "[x] - ";

      try {
        const body = content.slice(currentState.length).trim();
        await sendEvent(token, topic.name, `${newState}${body}`);
      } catch (err) {
        console.error("Failed to send reminder event:", err);
      }
    };

    const handleDelete = async (body) => {
      try {
        await sendEvent(token, topic.name, `[-] - ${body}`);
      } catch (err) {
        console.error("Failed to send delete reminder:", err);
      }
    };

    const handleAddReminder = async () => {
      const trimmed = newReminder.trim();
      if (!trimmed) return;
      try {
        await sendEvent(token, topic.name, `[] - ${trimmed}`);
        setNewReminder("");
      } catch (err) {
        console.error("Failed to add new reminder:", err);
      }
    };

    const sizeKey = `${w}x${h}`;
    const renderContent = () => {
      switch (sizeKey) {
        default:
          return (
            <div className="flex flex-col w-full h-full">
              <CardTitle title={topic.name} />
              <div className="flex-1 overflow-y-auto space-y-2 mt-2">
                {finalReminders.map((msg, idx) => {
                  const match = msg.content.match(/^\[.*\] - /);
                  const state = match ? match[0] : "";
                  const body = msg.content.slice(state.length).trim();

                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between"
                    >
                      <a
                        className="cursor-pointer"
                        onClick={() => handleToggle(msg.content, state)}
                      >
                        {getStateIcon(msg.content)}
                      </a>
                      <span className="text-sm text-gray-800 ml-2 flex-1">
                        {body}
                      </span>
                      <a
                        className="ml-2 text-gray-500 cursor-pointer text-lg leading-none"
                        onClick={() => handleDelete(body)}
                      >
                        ×
                      </a>
                    </div>
                  );
                })}
              </div>
              <div className="pt-2 flex items-center gap-2">
                <input
                  type="text"
                  className="border border-gray-300 rounded-full px-3 py-1.5 text-sm w-full"
                  placeholder="Add a reminder..."
                  value={newReminder}
                  onChange={(e) => setNewReminder(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddReminder();
                  }}
                />
                <button
                  className="bg-delft-blue hover:opacity-80 text-white p-2 rounded-full"
                  onClick={handleAddReminder}
                >
                  <FaArrowRight />
                </button>
              </div>
            </div>
          );
      }
    };

    return (
      <div className="p-2 shadow-md cursor-pointer transition-transform duration-300 w-full h-full flex flex-col bg-gray-50">
        {renderContent()}
      </div>
    );
  }
);

ReminderCard.variants = ["default"];
ReminderCard.variantParameters = {
  default: {},
};

ReminderCard.propTypes = {
  card: PropTypes.object.isRequired,
  topic: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  w: PropTypes.number.isRequired,
  h: PropTypes.number.isRequired,
};

export default ReminderCard;
